import { useMemo } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { PaymentPlansResponse } from "@covertree/new-business-graphql";

import { useCheckout } from "../CheckoutProvider";
import { PaymentPlanKey } from "../types";
import { getTotalCostFromPaymentPlan } from "../utils";

import { PaymentTermCard } from "./PaymentTermCard";

interface Props {
  onPaymentTermSelect: (paymentPlanKey: PaymentPlanKey) => void;
  paymentPlans: PaymentPlansResponse;
  selectedPaymentPlanKey: PaymentPlanKey;
}

export function PaymentTerm({
  onPaymentTermSelect,
  paymentPlans,
  selectedPaymentPlanKey,
}: Props) {
  const { anythingLoading } = useCheckout();

  const termItems = useMemo(() => {
    if (!paymentPlans) return [];

    const isMortgageBillSelected = selectedPaymentPlanKey === "mortgageBill";

    const plans: Record<PaymentPlanKey, { title: string; helpText?: string }> =
      {
        annual: {
          title: "Annual",
          helpText: "save 5%",
        },
        mortgageBill: {
          title: "Annual",
          helpText: "save 5%",
        },
        semiAnnual: {
          title: "Semi-Annual",
          helpText: "2 payments",
        },
        monthly: {
          title: "Monthly Payment Plan",
          helpText: "11 payments",
        },
      };

    return Object.entries(plans)
      .filter(
        ([key]) =>
          paymentPlans[key as PaymentPlanKey] &&
          (isMortgageBillSelected ? key !== "annual" : key !== "mortgageBill")
      )
      .map(([key, { title, helpText }]) => {
        const plan = paymentPlans[key as PaymentPlanKey]!;
        const discountHelpText = plan.discountRate
          ? `save ${plan.discountRate * 100}%`
          : undefined;

        const totalCost = getTotalCostFromPaymentPlan(plan);

        return {
          helpText: discountHelpText ?? helpText,
          title,
          paymentPlanKey: key as PaymentPlanKey,
          totalCost: totalCost.toString(),
          disabled:
            !plan.isAvailable ||
            (isMortgageBillSelected && key !== "mortgageBill"),
          selected: selectedPaymentPlanKey === key,
          isAvailable: plan.isAvailable,
        };
      });
  }, [paymentPlans, selectedPaymentPlanKey]);

  return (
    <Box sx={{ marginTop: { xs: 3, md: 1 } }}>
      <Typography variant="h6" gutterBottom>
        Select a payment term
      </Typography>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          marginBottom: { xs: 3, md: 0 },
        }}>
        {termItems.map((item) => (
          <PaymentTermCard
            {...item}
            key={item.paymentPlanKey}
            disabled={item.disabled || anythingLoading}
            onPaymentTermSelect={onPaymentTermSelect}
          />
        ))}
      </Stack>
    </Box>
  );
}
